// components/steps/StepComponents.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Input,
  Button,
  Select,
  Typography,
  Space,
  Tooltip,
  notification,
  Checkbox,
  Divider,
  Modal,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  SafetyOutlined,
  LockOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  MailOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { FormData } from "../../types/inscription";
import { Form } from "antd";
import { CustomDatePicker } from "./CustomDatePicker";

const { Text } = Typography;
const { Option } = Select;

interface StepProps {
  formData: FormData;
  loading: boolean;
  isRTL: boolean;
  onFieldChange: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  errors?: Record<string, string>;
}

interface PhoneStepProps extends StepProps {
  onSendOTP: () => Promise<void>;
}

interface OTPStepProps extends StepProps {
  isOtpVerified: boolean;
  onVerifyOTP: (code: string) => Promise<void>;
  onSendEmailOTP?: (email: string) => Promise<boolean>;
}

export const TermsStep: React.FC<StepProps & { onAccept?: () => void }> = ({
  formData,
  loading,
  isRTL,
  onFieldChange,
  onNext,
  onAccept,
}) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [checked, setChecked] = useState(false);

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleAccept = () => {
    setShowModal(false);
    setChecked(true);
    if (onAccept) {
      onAccept();
    } else {
      onFieldChange("termsAccepted", true);
      setTimeout(() => onNext(), 300);
    }
  };

  const handleDecline = () => {
    setShowModal(false);
    setChecked(false);
    onFieldChange("termsAccepted", false);
  };

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div
          style={{
            padding: "24px",
            backgroundColor: "#f0f9ff",
            borderRadius: 8,
            border: "1px solid #91d5ff",
            textAlign: "center",
          }}
        >
          <FileTextOutlined
            style={{ fontSize: 48, color: "#1890ff", marginBottom: 16 }}
          />
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            {t("inscription.termsTitle") ||
              "Conditions d'utilisation & Confidentialité"}
          </div>
          <div style={{ fontSize: 14, color: "#666", marginBottom: 16 }}>
            {t("inscription.termsDescription") ||
              "Veuillez lire et accepter nos conditions d'utilisation pour continuer"}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            type="primary"
            size="large"
            icon={<FileTextOutlined />}
            onClick={handleOpenModal}
            style={{
              minWidth: 200,
              height: 48,
              borderRadius: 8,
              background: "#1890ff",
              borderColor: "#1890ff",
              fontSize: 16,
            }}
          >
            {t("inscription.buttons.viewTerms") ||
              "Voir les conditions d'utilisation"}
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 16,
            padding: "12px",
            backgroundColor: "#fff9e6",
            borderRadius: 6,
            border: "1px solid #ffe58f",
          }}
        >
          <Checkbox
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ marginTop: 4 }}
          />
          <Text style={{ flex: 1 }}>
            {t("inscription.iAccept") ||
              "J'accepte les conditions d'utilisation et la politique de confidentialité."}
          </Text>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          {/* <Button onClick={handleDecline} size="large">
            {t("inscription.decline") || "Refuser"}
          </Button> */}
          <Button
            type="primary"
            disabled={!checked}
            onClick={handleAccept}
            size="large"
            style={{
              background: checked ? "#1890ff" : undefined,
              borderColor: checked ? "#1890ff" : undefined,
            }}
          >
            {t("inscription.accept") || "J'accepte"}
          </Button>
        </div>
        {formData.termsAccepted && (
          <div
            style={{
              padding: "12px 16px",
              backgroundColor: "#f6ffed",
              border: "1px solid #b7eb8f",
              borderRadius: 6,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
            <Text style={{ color: "#52c41a", fontWeight: 500 }}>
              {t("inscription.termsAcceptedMessage") ||
                "Conditions acceptées avec succès"}
            </Text>
          </div>
        )}
      </Space>

      <Modal
        title={
          <div style={{ textAlign: isRTL ? "right" : "left" }}>
            {t("inscription.termsTitle") ||
              "Conditions d'utilisation & Confidentialité"}
          </div>
        }
        open={showModal}
        closable={true}
        onCancel={handleDecline}
        maskClosable={false}
        footer={null}
        width={Math.min(600, window.innerWidth * 0.9)}
        style={{
          direction: isRTL ? "rtl" : "ltr",
        }}
        bodyStyle={{
          direction: isRTL ? "rtl" : "ltr",
          textAlign: isRTL ? "right" : "left",
        }}
      >
        <div
          style={{
            maxHeight: "55vh",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            padding: "8px 0",
            marginBottom: 16,
          }}
        >
          {t("inscription.PRIVACY_TEXT") || "Termes et conditions..."}
        </div>

        <Divider style={{ margin: "16px 0" }} />

        {/* <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 16,
            padding: "12px",
            backgroundColor: "#fff9e6",
            borderRadius: 6,
            border: "1px solid #ffe58f",
          }}
        >
          <Checkbox
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ marginTop: 4 }}
          />
          <Text style={{ flex: 1 }}>
            {t("inscription.iAccept") ||
              "J'accepte les conditions d'utilisation et la politique de confidentialité."}
          </Text>
        </div> */}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button onClick={() => setShowModal(false)} size="large">
            {t("inscription.close") || "fermer"}
          </Button>
          {/* <Button onClick={handleDecline} size="large">
            {t("inscription.decline") || "Refuser"}
          </Button>
          <Button
            type="primary"
            disabled={!checked}
            onClick={handleAccept}
            size="large"
            style={{
              background: checked ? "#1890ff" : undefined,
              borderColor: checked ? "#1890ff" : undefined,
            }}
          >
            {t("inscription.accept") || "J'accepte"}
          </Button> */}
        </div>
      </Modal>
    </>
  );
};

export const PersonalInfoStep: React.FC<StepProps> = ({
  formData,
  loading,
  isRTL,
  onFieldChange,
  onNext,
  errors,
}) => {
  const { t } = useTranslation();

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: "100%", position: "relative" }}
    >
      <Form.Item
        validateStatus={errors?.fullName ? "error" : undefined}
        help={errors?.fullName}
        style={{ marginBottom: 0 }}
      >
        <Tooltip
          title={
            t("inscription.step1.fullName.helperText") ||
            "Entrez votre nom complet tel qu'il apparaît sur vos documents officiels"
          }
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t("inscription.step1.fullName.label") || "Full Name"}
            size="large"
            value={formData.fullName}
            onChange={(e) => onFieldChange("fullName", e.target.value)}
            suffix={
              formData.fullName.trim() && (
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
              )
            }
          />
        </Tooltip>
      </Form.Item>

      <Form.Item
        validateStatus={errors?.birthDate ? "error" : undefined}
        help={errors?.birthDate}
        style={{ marginBottom: 0 }}
      >
        <div>
          <div
            style={{
              marginBottom: 8,
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              flexDirection: isRTL ? "row-reverse" : "row",
              justifyContent: isRTL ? "flex-end" : "flex-start",
              gap: "4px",
            }}
          >
            {t("inscription.step1.birthDate.label")}
            <Tooltip title={t("inscription.step1.birthDate.helperText")}>
              <InfoCircleOutlined
                style={{
                  marginLeft: isRTL ? 0 : 18,
                  marginRight: isRTL ? 8 : 0,
                  color: "#999",
                }}
              />
            </Tooltip>
          </div>

          <CustomDatePicker
            placeholder={
              t("inscription.step1.birthDate.placeholder") ||
              "Select birth date"
            }
            value={formData.birthDate}
            onChange={(date) => onFieldChange("birthDate", date)}
            isRTL={isRTL}
          />
        </div>
      </Form.Item>

      <Form.Item
        validateStatus={errors?.gender ? "error" : undefined}
        help={errors?.gender}
        style={{ marginBottom: 0 }}
      >
        <div style={{ position: "relative" }}>
          <Select
            placeholder={
              t("inscription.step1.gender.placeholder") || "Select gender"
            }
            size="large"
            value={formData.gender || undefined}
            onChange={(value) => onFieldChange("gender", value)}
            style={{ width: "100%" }}
          >
            <Option value="MALE">
              {t("inscription.step1.gender.options.homme")}
            </Option>
            <Option value="FEMALE">
              {t("inscription.step1.gender.options.femme")}
            </Option>
          </Select>

          {formData.gender && (
            <CheckCircleOutlined
              style={{
                color: "#52c41a",
                position: "absolute",
                [isRTL ? "left" : "right"]: 32,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      </Form.Item>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          type="primary"
          onClick={onNext}
          loading={loading}
          size="large"
          style={{
            minWidth: 140,
            borderRadius: 8,
            background: "#1890ff",
            borderColor: "#1890ff",
          }}
        >
          {loading ? "Chargement..." : t("inscription.buttons.next")}
        </Button>
      </div>
    </Space>
  );
};

export const PhoneVerificationStep: React.FC<PhoneStepProps> = ({
  formData,
  loading,
  isRTL,
  onFieldChange,
  onSendOTP,
  errors,
}) => {
  const { t } = useTranslation();

  const phoneRaw = formData.phoneNumber || "";
  const phonePattern = /^[234]\d{7}$/;
  const isPhoneValid = phonePattern.test(phoneRaw);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/\D/g, "");

    if (raw.length === 1 && !/^[234]$/.test(raw)) {
      return;
    }

    if (raw.length > 8) raw = raw.slice(0, 8);

    onFieldChange("phoneNumber", raw);
  };

  const onRequestOtp = async () => {
    if (!isPhoneValid) {
      notification.error({
        message: t("inscription.validation.phoneInvalid") || "Numéro invalide",
        description:
          t("inscription.validation.phonePattern") ||
          "Doit contenir 8 chiffres et commencer par 2, 3 ou 4",
        placement: "topRight",
      });
      return;
    }
    try {
      await onSendOTP();
    } catch (err) {
      console.error("Failed to send verification code", err);
      notification.error({
        message: t("inscription.notifications.otpSendFailed"),
      });
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: "#f6ffed",
          border: "1px solid #b7eb8f",
          borderRadius: 6,
          fontSize: 14,
          textAlign: isRTL ? "right" : "left",
        }}
      >
        <InfoCircleOutlined
          style={{
            color: "#52c41a",
            [isRTL ? "marginLeft" : "marginRight"]: 8,
          }}
        />
        {t("inscription.step2.phoneNumber.helperText")}
      </div>

      <Form.Item
        validateStatus={
          phoneRaw ? (isPhoneValid ? undefined : "error") : undefined
        }
        help={
          phoneRaw && !isPhoneValid
            ? t("inscription.validation.phonePattern") ||
              "Doit contenir 8 chiffres et commencer par 2, 3 ou 4"
            : undefined
        }
        style={{ marginBottom: 0 }}
        hasFeedback
      >
        <Input
          prefix={<PhoneOutlined />}
          placeholder={t("inscription.step2.phoneNumber.placeholder")}
          size="large"
          value={formData.phoneNumber}
          onChange={handleChange}
          suffix={
            isPhoneValid && <CheckCircleOutlined style={{ color: "#52c41a" }} />
          }
          maxLength={8}
        />
      </Form.Item>

      <Button
        type="primary"
        size="large"
        disabled={!isPhoneValid || loading}
        loading={loading}
        onClick={onRequestOtp}
        style={{ width: "100%" }}
      >
        {t("inscription.buttons.requestOtp.label") || "Demander Code OTP"}
      </Button>
    </Space>
  );
};

export const OTPVerificationStep: React.FC<OTPStepProps> = ({
  formData,
  loading,
  onFieldChange,
  onVerifyOTP,
  onNext,
  isOtpVerified,
  onSendEmailOTP,
}) => {
  const { t } = useTranslation();
  const [verificationMethod, setVerificationMethod] = useState<
    "phone" | "email"
  >("phone");
  const [emailForOTP, setEmailForOTP] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleSendEmailOTP = async () => {
    if (!emailForOTP || !onSendEmailOTP) return;

    if (!emailRegex.test(emailForOTP)) {
      setEmailError(t("inscription.step3.verificationCode.invalidEmail"));
      return;
    }

    setSendingEmail(true);
    setEmailError(null);
    try {
      await onSendEmailOTP(emailForOTP);
      setEmailSent(true);
      onFieldChange("email", emailForOTP);
    } catch (error) {
      console.error("Failed to send email OTP:", error);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleEditEmail = () => {
    setEmailSent(false);
    onFieldChange("verificationCode", "");
  };

  const getCurrentContactInfo = () => {
    return verificationMethod === "phone"
      ? formData.phoneNumber
      : emailForOTP || formData.email;
  };

  const getCurrentIcon = () => {
    return verificationMethod === "phone" ? (
      <PhoneOutlined />
    ) : (
      <MailOutlined />
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div
        style={{
          textAlign: "center",
          padding: "16px",
          backgroundColor: "#f0f9ff",
          borderRadius: 8,
          border: "1px solid #91d5ff",
        }}
      >
        <SafetyOutlined
          style={{ fontSize: 24, color: "#1890ff", marginBottom: 8 }}
        />
        <div>
          {verificationMethod === "phone"
            ? t("inscription.step3.verificationCode.helperText")
            : "Nous avons envoyé un code de vérification à votre email"}
        </div>
        <div
          style={{
            fontWeight: 600,
            color: "#1890ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {getCurrentIcon()}
          {getCurrentContactInfo()}
        </div>
      </div>

      {verificationMethod === "email" && !emailSent && (
        <div>
          <Form.Item
            validateStatus={emailError ? "error" : ""}
            help={emailError || ""}
            style={{ marginBottom: 16 }}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Entrez votre adresse email"
              size="large"
              value={emailForOTP}
              onChange={(e) => {
                setEmailForOTP(e.target.value);
                setEmailError(null);
              }}
            />
          </Form.Item>

          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Button
              type="primary"
              onClick={handleSendEmailOTP}
              loading={sendingEmail}
              disabled={!emailForOTP}
              size="large"
              style={{
                borderRadius: 8,
                background: "#1890ff",
                borderColor: "#1890ff",
              }}
            >
              {sendingEmail ? "Envoi..." : "Envoyer le code"}
            </Button>
          </div>
        </div>
      )}

      {(verificationMethod === "phone" ||
        (verificationMethod === "email" && emailSent)) && (
        <>
          <Input
            prefix={<SafetyOutlined />}
            placeholder={t("inscription.step3.verificationCode.placeholder")}
            size="large"
            maxLength={6}
            value={formData.verificationCode}
            onChange={async (e) => {
              const value = e.target.value.replace(/\D/g, "");
              onFieldChange("verificationCode", value);

              if (value.length === 6) {
                await onVerifyOTP(value);
              }
            }}
            suffix={
              formData.verificationCode.length === 6 && (
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
              )
            }
            style={{
              textAlign: "center",
              fontSize: 16,
              letterSpacing: 4,
              fontWeight: 400,
            }}
          />

          {verificationMethod === "phone" && (
            <div style={{ textAlign: "center" }}>
              <Button
                type="link"
                onClick={() => setVerificationMethod("email")}
                style={{ color: "#1890ff", padding: 0 }}
              >
                <MailOutlined style={{ marginRight: 4 }} />
                Recevoir le code par email
              </Button>
            </div>
          )}

          {verificationMethod === "email" && emailSent && (
            <div style={{ textAlign: "center" }}>
              <Button
                type="link"
                onClick={handleEditEmail}
                style={{ color: "#1890ff", padding: 0 }}
              >
                <MailOutlined style={{ marginRight: 4 }} />
                Recevoir le code par email
              </Button>
            </div>
          )}
        </>
      )}

      <div style={{ textAlign: "center" }}>
        {isOtpVerified && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              type="primary"
              onClick={onNext}
              loading={loading}
              size="large"
              style={{
                minWidth: 140,
                borderRadius: 8,
                background: "#1890ff",
                borderColor: "#1890ff",
              }}
            >
              {loading ? "Chargement..." : t("inscription.buttons.next")}
            </Button>
          </div>
        )}
      </div>
    </Space>
  );
};

export const PasswordStep: React.FC<StepProps> = ({
  formData,
  loading,
  isRTL,
  onFieldChange,
  onNext,
}) => {
  const { t } = useTranslation();

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: "#fffbe6",
          border: "1px solid #ffe58f",
          borderRadius: 6,
          fontSize: 14,
          textAlign: isRTL ? "right" : "left",
        }}
      >
        <LockOutlined
          style={{
            color: "#faad14",
            [isRTL ? "marginLeft" : "marginRight"]: 8,
          }}
        />
        {t("inscription.step4.passwordDescription")}
      </div>

      <Input.Password
        prefix={<LockOutlined />}
        placeholder={
          t("inscription.step4.password.placeholder") ||
          "Create numeric password"
        }
        size="large"
        value={formData.password}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, "");
          onFieldChange("password", value);
        }}
        maxLength={4}
        iconRender={(visible) =>
          visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
        }
      />

      <Input.Password
        prefix={<LockOutlined />}
        placeholder={
          t("inscription.step4.confirmPassword.placeholder") ||
          "Confirm password"
        }
        size="large"
        value={formData.confirmPassword}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, "");
          onFieldChange("confirmPassword", value);
        }}
        maxLength={4}
        iconRender={(visible) =>
          visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
        }
        suffix={
          formData.password &&
          formData.confirmPassword &&
          formData.password === formData.confirmPassword ? (
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
          ) : null
        }
        onPressEnter={onNext}
      />

      {formData.password &&
        formData.confirmPassword &&
        formData.password !== formData.confirmPassword && (
          <Text type="danger" style={{ fontSize: 12 }}>
            {t("inscription.validation.passwordMatch") ||
              "Passwords do not match"}
          </Text>
        )}

      {formData.password && formData.password.length < 4 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("inscription.validation.passwordLength", { min: 4 }) ||
            "Password must be at least 4 digits"}
        </Text>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          type="primary"
          onClick={onNext}
          loading={loading}
          size="large"
          style={{
            minWidth: 140,
            borderRadius: 8,
            background: "#52c41a",
            borderColor: "#52c41a",
          }}
        >
          {loading ? "Chargement..." : t("inscription.buttons.finish")}
        </Button>
      </div>
    </Space>
  );
};
